/**
 * IdeaDetailView
 *
 * Full-page chrome over ``useIdeaDetailContent`` — the third mount of the
 * chrome-agnostic idea content hook (alongside the admin modal and the
 * portal drawer). The page is the SMM workspace cockpit: the idea's
 * pills + property/action header (the hook header) sits in the Concept
 * panel's subheader slot (DD-010), fixed above the scrolling body, under
 * the header trail (published via ``useDetailTrail``),
 * and the full set of working panels — Concept, Media, Posts, Impact — ALL
 * present at once, each minimisable. No stage switching: everything is
 * available, you collapse what you are not using.
 *
 * Chrome principle: page for the SMM, modal for the client. The modal and
 * drawer keep using the same hook unchanged.
 */

import React, { useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { useDetailTrail } from "@/core/navigation/TrailDisplayContext";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { useAppStore } from "@/core/store/useAppStore";
import { PageLayout } from "@/components/PageLayout";
import { TabGroup, TabItem, TabGroupConfig } from "@/components/ui/TabGroup";
import { SpinnerIcon, ImageIcon, PostIcon, LightbulbIcon, ChartBarIcon } from "@/components/icons";
import { useIdeaDetailContent } from "./components/useIdeaDetailContent";
import { WorkspacePanel } from "./components/WorkspacePanel";
import { IdeaImpactPanel } from "./components/IdeaImpactPanel";
import { EditorPostCard } from "@/domains/PostCreator/cards/EditorPostCard";
import { postsService } from "@/domains/PostCreator/services/postsService";
import { AdminMediaCard, AudioMediaCard } from "@/components/MediaCard";
import { ImagePreviewModal } from "@/domains/LibraryView/components/ImagePreviewModal";
import { VideoPreviewModal } from "@/domains/LibraryView/components/VideoPreviewModal";
import { AudioPlayer } from "@/components/ui/AudioPlayer";
import { AIImageEditorModal } from "@/components/ImageEditor/AIImageEditorModal";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { UserRole } from "@/services/urlService";
import { Post } from "@/core/types/postTypes";
import { ideaKeys } from "./types";
import { useIdeaQuery } from "./hooks/useIdeas";
import { useIdeaCollection } from "./hooks/useIdeaCollection";
import * as collectionsService from "@/services/collectionsService";
import type { CollectionMediaItem, MediaTypeCounts } from "@/services/collectionsService";
import { summarizePostsProgress } from "./ideaStageState";
import { MediaType, tileFor, gridClassFor } from "./ideaMediaTiles";
import { useObjectiveTypes, resolveObjectiveLabel } from "./services/objectiveTypes";

type PanelId = "concept" | "media" | "posts" | "impact";

const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  photo: "Photos",
  video: "Videos",
  audio: "Audio",
};

/** Media stage: the idea's collection contents, browsed by media type. */
const MediaStage: React.FC<{
  clientId: string;
  collectionId: string | null;
  counts: MediaTypeCounts;
}> = ({ clientId, collectionId, counts }) => {
  const presentTypes = (Object.keys(MEDIA_TYPE_LABELS) as MediaType[]).filter(
    (t) => (counts[t] ?? 0) > 0
  );
  const [activeType, setActiveType] = useState<MediaType | null>(null);
  const effectiveType = activeType ?? presentTypes[0] ?? null;

  const queryClient = useQueryClient();
  const addToast = useAppStore((s) => s.addToast);
  const [previewItem, setPreviewItem] = useState<MediaItem | null>(null);
  const [aiEditingItem, setAiEditingItem] = useState<MediaItem | null>(null);
  const [playingAudio, setPlayingAudio] = useState<MediaItem | null>(null);

  // Card actions (delete, restage) mutate the idea's collection — refresh both
  // the browsed items and the per-type count badges the parent panel shows.
  const invalidateMedia = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["idea-collection-items", collectionId] });
    queryClient.invalidateQueries({ queryKey: ["idea-media-type-counts", collectionId] });
  }, [queryClient, collectionId]);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["idea-collection-items", collectionId, effectiveType],
    queryFn: async (): Promise<CollectionMediaItem[]> => {
      if (!collectionId || !effectiveType) return [];
      const res = await collectionsService.getCollectionItems(clientId, collectionId, {
        mediaType: effectiveType,
        limit: 60,
      });
      return res.items;
    },
    enabled: Boolean(collectionId && effectiveType),
  });

  // effectiveType is null only when presentTypes is empty, so this one guard
  // covers the empty state and narrows the type for everything below.
  if (!collectionId || presentTypes.length === 0 || !effectiveType) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <ImageIcon className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm text-gray-500">No media yet.</p>
        <p className="text-xs text-gray-400 mt-1">
          Use Upload in the action bar above to add media to this idea.
        </p>
      </div>
    );
  }

  const tabItems: TabItem[] = presentTypes.map((t) => ({
    id: t,
    label: MEDIA_TYPE_LABELS[t],
    badge: counts[t] ?? 0,
  }));

  const tabConfig: TabGroupConfig = {
    variant: "navigation",
    size: "sm",
    layout: "horizontal",
    fullWidth: false,
    hasBackground: true,
  };

  return (
    <div className="space-y-4">
      <div className="w-fit">
        <TabGroup
          items={tabItems}
          activeId={effectiveType}
          onTabChange={(id) => setActiveType(id as MediaType)}
          config={tabConfig}
        />
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <SpinnerIcon className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-500 py-8 text-center">No items of this type.</p>
      ) : (
        <div className={gridClassFor(effectiveType)}>
          {items.map((item) => {
            const mediaItem = MediaItem.fromBackend(item, UserRole.ADMIN, clientId);

            // Audio carries no thumbnail, so it takes the MediaCard system's
            // non-thumbnail tile (TD-001): filename, inline play, duration.
            if (tileFor(effectiveType) === "audio") {
              return (
                <AudioMediaCard
                  key={item.id}
                  item={mediaItem}
                  isSelected={false}
                  onSelect={() => {}}
                  onClick={() => setPlayingAudio(mediaItem)}
                  onStageChange={invalidateMedia}
                  onMediaItemDeleted={invalidateMedia}
                  onActionError={(msg) => addToast({ type: "error", message: msg })}
                />
              );
            }

            return (
              <AdminMediaCard
                key={item.id}
                mediaItem={mediaItem}
                clientId={clientId}
                aspectRatio={effectiveType === "video" ? "9:16" : "1:1"}
                onClick={() => setPreviewItem(mediaItem)}
                onStageChange={invalidateMedia}
                onMediaItemDeleted={invalidateMedia}
                onActionError={(msg) => addToast({ type: "error", message: msg })}
              />
            );
          })}
        </div>
      )}

      {/* Click-to-detail modals — reuse the Library's per-type previews, so a
          tile behaves the same here as it does in the library. */}
      {effectiveType === "photo" && (
        <>
          <ImagePreviewModal
            isOpen={!!previewItem}
            onClose={() => setPreviewItem(null)}
            mediaItem={previewItem}
            clientId={clientId}
            onEditWithAI={(item) => {
              setPreviewItem(null);
              setAiEditingItem(item);
            }}
          />
          <AIImageEditorModal
            isOpen={!!aiEditingItem}
            onClose={() => setAiEditingItem(null)}
            mediaItem={aiEditingItem}
            clientId={clientId}
          />
        </>
      )}
      {effectiveType === "video" && (
        <VideoPreviewModal
          isOpen={!!previewItem}
          onClose={() => setPreviewItem(null)}
          mediaItem={previewItem}
          clientId={clientId}
        />
      )}
      {/* Not gated on the active tab: switching tabs mid-listen should not
          yank the player out from under you. */}
      {playingAudio && (
        <AudioPlayer
          isOpen
          onClose={() => setPlayingAudio(null)}
          src={playingAudio.getAudioStreamUrl()}
          title={
            playingAudio.originalFilename?.replace(/\.[^/.]+$/, "") || "Audio"
          }
        />
      )}
    </div>
  );
};

export const IdeaDetailView: React.FC = () => {
  const { ideaId } = useParams<{ ideaId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedClient } = useAppStore();
  const clientId = selectedClient?.id;
  const queryClient = useQueryClient();

  // A linked post changed status or was deleted from its card. The panel
  // fetches posts by individual key (not the shared list cache), so refresh
  // those queries plus the idea detail (drives the grid + published/total).
  const handleLinkedPostChanged = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["idea-linked-post", clientId] });
    if (ideaId) queryClient.invalidateQueries({ queryKey: ideaKeys.detail(ideaId) });
  }, [queryClient, clientId, ideaId]);

  // All panels open by default; the SMM minimises what they aren't using.
  const [collapsed, setCollapsed] = useState<Record<PanelId, boolean>>({
    concept: false,
    media: false,
    posts: false,
    impact: false,
  });
  const toggle = (id: PanelId) =>
    setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  const { data: idea } = useIdeaQuery(ideaId, true, true, true);

  // Header trail: returns to wherever the idea was opened from (its list,
  // Home, a brief, ...); falls back to the ideas list on deep link/refresh.
  const { goBack, navOptions } = useDetailTrail({
    fallback: {
      to: location.pathname.replace(/\/[^/]+$/, ""),
      label: ENTITY_CONFIG.idea.plural,
    },
    title: idea?.title?.trim() || "Idea",
  });

  const content = useIdeaDetailContent({
    ideaId: ideaId ?? null,
    clientId: clientId ?? "",
    isOpen: true,
    onClose: goBack,
    onDelete: goBack,
    actionsPlacement: "panels",
    trailNavOptions: navOptions,
  });

  const { collectionId } = useIdeaCollection(ideaId ?? null, clientId ?? "");
  const { data: objectiveTypes = [] } = useObjectiveTypes();

  const { data: mediaCounts = {} as MediaTypeCounts } = useQuery({
    queryKey: ["idea-media-type-counts", collectionId],
    queryFn: async (): Promise<MediaTypeCounts> => {
      if (!collectionId || !clientId) return {};
      return collectionsService.getCollectionMediaTypeCounts(clientId, collectionId);
    },
    enabled: Boolean(collectionId && clientId),
  });

  // Fetch ONLY the idea's linked posts (scoped), capped for render — avoids
  // loading every client post to show a few, and bounds the 200+ outlier.
  const POSTS_RENDER_CAP = 24;
  const totalLinkedPosts = idea?.posts?.length ?? 0;
  const renderPostIds = useMemo(
    () => (idea?.posts ?? []).map((p) => p.id).slice(0, POSTS_RENDER_CAP),
    [idea?.posts]
  );
  const linkedPostQueries = useQueries({
    queries: renderPostIds.map((postId) => ({
      queryKey: ["idea-linked-post", clientId, postId],
      queryFn: () => postsService.getPost(clientId as string, postId),
      enabled: Boolean(clientId && postId),
      staleTime: 60_000,
    })),
  });
  const linkedPosts = useMemo(
    () => linkedPostQueries.map((q) => q.data).filter(Boolean) as Post[],
    [linkedPostQueries]
  );

  const mediaTotal =
    (mediaCounts.photo ?? 0) + (mediaCounts.video ?? 0) + (mediaCounts.audio ?? 0);
  const postsProgress = summarizePostsProgress(idea?.posts);

  const objectiveLabel = resolveObjectiveLabel(idea?.objectiveType, objectiveTypes);

  if (!selectedClient) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">No Client Selected</h2>
          <p className="text-gray-500">Please select a client to view this idea</p>
        </div>
      </div>
    );
  }

  if (content.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-gray-500">Loading idea...</div>
      </div>
    );
  }

  if (content.error || !content.hasIdea) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-red-600 mb-2">Error Loading Idea</h2>
          <p className="text-gray-500 mb-4">
            {content.error instanceof Error ? content.error.message : "Idea not found"}
          </p>
          <button onClick={goBack} className="text-blue-600 hover:text-blue-800">
            Back to Ideas
          </button>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      <div className="flex-1 flex flex-col min-h-0">
        {/* Cockpit — every panel present, each minimisable. Full width.
            Below xl the whole cockpit scrolls; from xl up it fills the
            space under the header and each column scrolls on its own, so
            panel heights come from the layout, not viewport math. */}
        <div className="flex-1 min-h-0 overflow-y-auto xl:overflow-hidden px-4 lg:px-6 py-5">
          <div className="flex flex-col xl:flex-row gap-5 items-start xl:h-full">
            {/* Concept — the writing column. Caps at the cockpit height;
                past that the panel body scrolls internally. */}
            <WorkspacePanel
              title="Concept"
              icon={<LightbulbIcon className="w-4 h-4" />}
              collapsed={collapsed.concept}
              onToggleCollapse={() => toggle("concept")}
              className="w-full xl:w-[38%] xl:max-w-xl xl:max-h-full"
              subheader={
                // Idea header — pills + title/property strip, fixed with the
                // panel header while the concept body scrolls (DD-010).
                <div className="space-y-3 pt-1">
                  {(objectiveLabel ||
                    (idea?.platforms?.length ?? 0) > 0 ||
                    (idea?.contentCategories?.length ?? 0) > 0) && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {objectiveLabel && (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium bg-indigo-50 text-indigo-700 rounded-full">
                          {objectiveLabel}
                        </span>
                      )}
                      {idea?.platforms?.map((platform) => (
                        <span
                          key={platform}
                          className="px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded-full"
                        >
                          {platform}
                        </span>
                      ))}
                      {idea?.contentCategories?.map((cat) => (
                        <span
                          key={cat}
                          className="px-2 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 rounded-full"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                  {content.header}
                </div>
              }
            >
              {content.body}
            </WorkspacePanel>

            {/* Right column — Media on top, Posts + Impact beneath. Scrolls
                independently of the Concept column from xl up. */}
            <div className="w-full xl:flex-1 flex flex-col gap-5 min-w-0 xl:max-h-full xl:overflow-y-auto">
              <WorkspacePanel
                title="Media"
                icon={<ImageIcon className="w-4 h-4" />}
                summary={mediaTotal > 0 ? `${mediaTotal} item${mediaTotal > 1 ? "s" : ""}` : "empty"}
                collapsed={collapsed.media}
                onToggleCollapse={() => toggle("media")}
                actions={content.mediaActions}
                className="shrink-0"
              >
                <MediaStage
                  clientId={clientId ?? ""}
                  collectionId={collectionId}
                  counts={mediaCounts}
                />
              </WorkspacePanel>

              <WorkspacePanel
                  title="Posts"
                  icon={<PostIcon className="w-4 h-4" />}
                  summary={
                    postsProgress.total > 0
                      ? `${postsProgress.published}/${postsProgress.total} published`
                      : "none yet"
                  }
                  collapsed={collapsed.posts}
                  onToggleCollapse={() => toggle("posts")}
                  className="w-full shrink-0"
                  actions={content.createPostAction}
                >
                  {linkedPosts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <PostIcon className="w-9 h-9 text-gray-300 mb-2.5" />
                      <p className="text-sm text-gray-500">No posts linked yet.</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Create a post to start shipping this idea.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-5 2xl:grid-cols-6 gap-2">
                        {linkedPosts.map((post) => (
                          <EditorPostCard
                            key={post.id}
                            post={post}
                            onStatusChange={handleLinkedPostChanged}
                            onPostDeleted={handleLinkedPostChanged}
                          />
                        ))}
                      </div>
                      {totalLinkedPosts > POSTS_RENDER_CAP && (
                        <button
                          type="button"
                          onClick={() => navigate("../../posts")}
                          className="text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          View all {totalLinkedPosts} posts
                        </button>
                      )}
                    </div>
                  )}
                </WorkspacePanel>

              <WorkspacePanel
                  title="Impact"
                  icon={<ChartBarIcon className="w-4 h-4" />}
                  summary="organic vs paid"
                  collapsed={collapsed.impact}
                  onToggleCollapse={() => toggle("impact")}
                  className="w-full shrink-0"
                >
                  <IdeaImpactPanel ideaId={ideaId} />
                </WorkspacePanel>
            </div>
          </div>
        </div>
      </div>
      {content.sideEffects}
    </PageLayout>
  );
};
